import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bars } from "react-loader-spinner";
import { Strings } from "../i18n";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { setUser } from "../redux/reducers/userReducer";
import UserService from "../services/userService";
import ClickableOpacityDiv from "../shared/components/ClickableOpacityDiv";
import OutlinedButton from "../shared/components/OutlinedButton";
import PrimaryButton from "../shared/components/PrimaryButton";
import { AppColors } from "../theme/AppTheme";
import ResumeUploadBox from "./components/ResumeUploadBox";
import IResume from "../shared/interfaces/IResume";
import SkipButton from "../shared/components/SkipButton";
import CloseButton from "../shared/components/CloseButton";

const StyledContainer = styled('div')((props: { isMobile: boolean }) => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: props.isMobile ? '100%' : '70%',
    height: props.isMobile ? '100%' : undefined,
    maxWidth: props.isMobile ? '100%' : '650px',
    maxHeight: props.isMobile ? '100%' : '760px',
    backgroundColor: AppColors.white,
    boxSizing: 'border-box',
}));

const StyledScrollingContainer = styled('div')((props: { isMobile: boolean }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: AppColors.white,
    padding: props.isMobile ? '44px 48px' : '54px 100px 64px',
    boxSizing: 'border-box',
}));

const StyledButtons = styled('div')((props: { isMobile: boolean, isOnboarding: boolean }) => ({
    padding: props.isOnboarding ? '20px 100px' : '20px',
    borderTop: `1px solid ${AppColors.grey.border}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '22px',
    width: props.isMobile ? 'calc(100% - 48px)' : '100%',
}));

interface OnboardingResumeScreenProps {
    replacing?: boolean;
    onDone?: () => void;
}

const OnboardingResumeScreen = (props: OnboardingResumeScreenProps) => {
    const { replacing, onDone } = props;
    const resume = useAppSelector(store => store.userState.currentUser?.resume)

    const [existingResume, setExistingResume] = useState<IResume | undefined>(replacing ? undefined : resume);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [file, setFile] = useState<File | undefined>(undefined);
    const isMobile = useAppSelector(store => store.appState.isMobile);
    const dispatch = useAppDispatch();

    const onSkipStepPressed = () => {
        onDone?.();
    }

    const onSubmitPressed = async () => {
        setError('');
        if (!!existingResume) {
            onDone?.();
            return;
        }
        setLoading(true);
        try {
            const updatedUser = await UserService.uploadResume(file!);
            dispatch(setUser(updatedUser));
            setSuccess(true);
            setTimeout(() => {
                onDone?.();
            }, 2000);

        } catch (e) {
            console.error(`${e}`);
            setError(`Something went wrong. Please try again.`);
        } finally {
            setLoading(false);
        }
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setExistingResume(undefined);
        const files = event.target.files;
        if (!!files && files.length > 0) {
            setFile(files[0]);
        }
    }

    const removeFile = () => {
        setFile(undefined);
        setExistingResume(undefined);
    }

    const isOnboarding = (!replacing && !existingResume);

    return (
        <StyledContainer isMobile={isMobile}>
            {isOnboarding && <SkipButton onClick={onSkipStepPressed} />}
            <StyledScrollingContainer isMobile={isMobile}>
                <Typography variant='h2' style={{ marginBottom: '21px' }}>
                    {replacing ? t(Strings.replaceResume) : t(Strings.uploadResume)}
                </Typography>
                <Typography variant='h5' style={{
                    textAlign: 'center',
                    marginBottom: '40px',
                    color: AppColors.grey.darkish
                }}>
                    {t(Strings.resumeInfo)}
                </Typography>
                <ResumeUploadBox existingResume={existingResume} file={file} onFileChanged={handleFileChange} onRemoveFile={removeFile} />
                {/* {!(replacing || existingResume) &&
                    <>
                        <span style={{ minHeight: '22px', maxHeight: '22px' }} />
                        <PrimaryButton title={t(Strings.addResumeButton).toUpperCase()} disabled={!file || loading || success} onClick={onSubmitPressed} />
                    </>
                } */}
                {error &&
                    <>
                        <span style={{ minHeight: '20px', maxHeight: '20px' }} />
                        <Typography variant='subtitle2' style={{ color: AppColors.error }}>
                            {error}
                        </Typography>
                    </>
                }
                {loading && <>
                    <span style={{ minHeight: '20px', maxHeight: '20px' }} />
                    <Bars
                        height="30"
                        width="30"
                        color={AppColors.pink.dark}
                        ariaLabel="bars-loading"
                        visible={true}
                    />
                </>}
                {success && <Typography variant='subtitle2' style={{ marginTop: '20px', color: AppColors.success }}>
                    Upload successful!
                </Typography>}
                {/* {(!replacing && !existingResume) && <Typography variant='h5' style={{ marginTop: '40px', display: 'flex', flexDirection: 'row' }}>
                    Or you can <ClickableOpacityDiv onClick={onSkipStepPressed} style={{ color: AppColors.link, marginLeft: '3px' }}>skip this step.</ClickableOpacityDiv>
                </Typography>} */}
            </StyledScrollingContainer>
            <StyledButtons isMobile={isMobile} isOnboarding={isOnboarding}>
                {!isOnboarding && <OutlinedButton title={t(Strings.cancel).toUpperCase()} disabled={false} onClick={onSkipStepPressed} />}
                <PrimaryButton title={!!existingResume ? 'Continue'.toUpperCase() : t(Strings.addResumeButton).toUpperCase()} disabled={(!file && !existingResume) || loading} onClick={onSubmitPressed} />
            </StyledButtons>
        </StyledContainer>
    )
}

export default OnboardingResumeScreen